// The upload-time estimator on /web-uploader (#estimate): photos × average original size over an upload
// speed — typed in, or measured with the one-tap speed test (never automatic: it uses the visitor's data).
// The model is src/lib/uploader/uploadEstimate.ts (shared with the web uploader); everything shown is
// labelled an estimate. No motion beyond a pulse that respects prefers-reduced-motion.
import { useEffect, useId, useRef, useState } from "react";
import { ExternalLink, Gauge, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { WebUploaderCopy } from "@/content/webUploader";
import {
  DEFAULT_CORES,
  DEFAULT_ORIGINAL_MB,
  EXAMPLE_SPEEDS_MBPS,
  MEASURED,
  PHOTO_PRESETS,
  estimateUpload,
  roundDuration,
  roundMbps,
} from "@/lib/uploader/uploadEstimate";
import { fill, formatDuration, formatNumber } from "@/lib/uploader/formatEstimate";
import { SPEED_TEST_MAX_MB, runSpeedTest } from "@/lib/uploader/speedTest";

/** A general speed test, as the fallback (it measures upload too). */
export const GENERAL_SPEED_TEST_URL = "https://speed.cloudflare.com/";

type EstimateCopy = WebUploaderCopy["estimate"];

const deviceCores = (): number => {
  const c = typeof navigator !== "undefined" ? navigator.hardwareConcurrency : undefined;
  return c && c > 0 ? c : DEFAULT_CORES;
};

const parse = (v: string): number => Number(String(v).replace(",", ".").trim());

type TestState = { kind: "idle" } | { kind: "running"; mbps: number | null } | { kind: "done"; mbps: number } | { kind: "failed" };

export function UploadEstimator({ copy, lang }: { copy: EstimateCopy; lang: string }) {
  const ids = useId();
  const [photos, setPhotos] = useState<number>(10_000);
  const [custom, setCustom] = useState<string>("");
  const [isCustom, setIsCustom] = useState(false);
  const [sizeMb, setSizeMb] = useState<string>(String(DEFAULT_ORIGINAL_MB));
  const [mbps, setMbps] = useState<string>(String(MEASURED.mbps));
  const [test, setTest] = useState<TestState>({ kind: "idle" });
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);

  const photoCount = isCustom ? Math.round(parse(custom)) : photos;
  const avgBytes = parse(sizeMb) * 1e6;
  const speed = parse(mbps);
  // The device's cores after mount (the server render assumes DEFAULT_CORES, so hydration matches).
  const [cores, setCores] = useState<number>(DEFAULT_CORES);
  useEffect(() => setCores(deviceCores()), []);
  const est = estimateUpload({ photos: photoCount, originalBytes: photoCount * avgBytes, mbps: speed, cores });

  const startTest = async () => {
    abort.current?.abort();
    const ac = new AbortController();
    abort.current = ac;
    setTest({ kind: "running", mbps: null });
    try {
      const r = await runSpeedTest({ signal: ac.signal, onProgress: (m) => setTest({ kind: "running", mbps: m }) });
      const m = roundMbps(r.mbps);
      setMbps(String(m));
      setTest({ kind: "done", mbps: m });
    } catch (e) {
      if (!ac.signal.aborted) setTest({ kind: "failed" });
    }
  };

  const dur = (s: number) => formatDuration(roundDuration(s), copy.time, lang);
  const gb = formatNumber(Math.round(((photoCount * avgBytes) / 1e9) * 10) / 10, lang);
  const examples = EXAMPLE_SPEEDS_MBPS.map((m) => ({
    mbps: m,
    e: estimateUpload({ photos: MEASURED.photos, originalBytes: MEASURED.photos * DEFAULT_ORIGINAL_MB * 1e6, mbps: m, cores: DEFAULT_CORES })!,
  }));
  const running = test.kind === "running";

  return (
    <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-12">
      {/* Inputs */}
      <form className="space-y-6 rounded-2xl border border-border bg-card p-5 sm:p-6" onSubmit={(e) => e.preventDefault()}>
        <fieldset>
          <legend className="mb-2 font-semibold">{copy.photos}</legend>
          <div className="flex flex-wrap gap-2" role="group">
            {PHOTO_PRESETS.map((n) => {
              const on = !isCustom && photos === n;
              return (
                <button
                  key={n}
                  type="button"
                  aria-pressed={on}
                  onClick={() => { setIsCustom(false); setPhotos(n); }}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${on ? "border-hawk-orange bg-hawk-orange text-white" : "border-border bg-background hover:border-hawk-orange/60"}`}
                >
                  {formatNumber(n, lang)}
                </button>
              );
            })}
            <button
              type="button"
              aria-pressed={isCustom}
              onClick={() => { setIsCustom(true); if (!custom) setCustom(String(photos)); }}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${isCustom ? "border-hawk-orange bg-hawk-orange text-white" : "border-border bg-background hover:border-hawk-orange/60"}`}
            >
              {copy.custom}
            </button>
          </div>
          {isCustom && (
            <input
              aria-label={copy.customLabel}
              type="number"
              inputMode="numeric"
              min={1}
              max={10_000_000}
              step={1}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              className="mt-3 h-10 w-40 rounded-md border border-input bg-background px-3 text-base"
            />
          )}
        </fieldset>

        <div>
          <label htmlFor={`${ids}-size`} className="mb-2 block font-semibold">{copy.size}</label>
          <div className="flex items-center gap-2">
            <input
              id={`${ids}-size`}
              type="number"
              inputMode="decimal"
              min={0.1}
              max={500}
              step={0.5}
              value={sizeMb}
              onChange={(e) => setSizeMb(e.target.value)}
              className="h-10 w-28 rounded-md border border-input bg-background px-3 text-base"
            />
            <span className="text-sm text-muted-foreground">{copy.sizeUnit}</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{fill(copy.sizeHint, { mb: formatNumber(DEFAULT_ORIGINAL_MB, lang) })}</p>
        </div>

        <div>
          <label htmlFor={`${ids}-speed`} className="mb-2 block font-semibold">{copy.speed}</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              id={`${ids}-speed`}
              type="number"
              inputMode="decimal"
              min={0.1}
              max={100_000}
              step="any"
              value={mbps}
              onChange={(e) => { setMbps(e.target.value); if (test.kind === "done") setTest({ kind: "idle" }); }}
              className="h-10 w-28 rounded-md border border-input bg-background px-3 text-base"
            />
            <span className="text-sm text-muted-foreground">{copy.speedUnit}</span>
            <Button type="button" variant="outline" onClick={startTest} disabled={running} className="ml-0 sm:ml-2">
              {running ? <Loader2 className="motion-safe:animate-spin" aria-hidden="true" /> : <Gauge aria-hidden="true" />}
              {copy.test}
            </Button>
          </div>
          <p className="mt-2 min-h-[1.25rem] text-sm font-medium" role="status" aria-live="polite" data-testid="speed-status">
            {test.kind === "running" && (test.mbps == null ? copy.testStarting : fill(copy.testing, { mbps: formatNumber(roundMbps(test.mbps), lang) }))}
            {test.kind === "done" && fill(copy.testDone, { mbps: formatNumber(test.mbps, lang) })}
            {test.kind === "failed" && <span className="text-destructive">{copy.testFailed}</span>}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {fill(copy.dataNote, { mb: formatNumber(SPEED_TEST_MAX_MB, lang) })}{" "}
            <a href={GENERAL_SPEED_TEST_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-hawk-orange hover:underline">
              {copy.otherTest}
              <ExternalLink size={13} aria-hidden="true" />
            </a>
          </p>
        </div>
      </form>

      {/* Results */}
      <div aria-live="polite" data-testid="estimate-results">
        <h3 className="mb-4 font-display text-xl font-bold">{copy.resultsHeading}</h3>
        {est ? (
          <div className="space-y-4">
            <div className="rounded-2xl border-2 border-hawk-orange/50 bg-hawk-orange/5 p-5" data-testid="estimate-previews">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="font-semibold">{copy.previewsLabel}</span>
                <span className="rounded-full border border-hawk-orange/40 bg-hawk-orange/10 px-2.5 py-0.5 text-xs font-semibold text-hawk-orange">{copy.previewsPlans}</span>
              </div>
              <p className="font-display text-2xl font-bold">{fill(copy.previewsLive, { time: dur(est.liveWithPreviews) })}</p>
              <p className="mt-1 text-sm text-muted-foreground">{fill(copy.previewsAll, { time: dur(est.allOriginalsWithPreviews) })}</p>
              {est.previewsComputerBound && <p className="mt-2 text-xs text-muted-foreground">{copy.computerNote}</p>}
            </div>
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="estimate-originals">
              <p className="mb-2 font-semibold">{copy.originalsLabel}</p>
              <p className="font-display text-2xl font-bold">{fill(copy.originalsLive, { time: dur(est.liveOriginalsOnly) })}</p>
              <p className="mt-1 text-sm text-muted-foreground">{fill(copy.originalsWhen, { gb })}</p>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">—</p>
        )}
        <p className="mt-4 text-xs text-muted-foreground">{copy.disclaimer}</p>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="estimate-examples">
            <caption className="mb-2 text-left font-semibold">{copy.table.caption}</caption>
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th scope="col" className="py-2 pr-3 font-medium">{copy.table.speed}</th>
                <th scope="col" className="py-2 pr-3 font-medium">{copy.table.live}</th>
                <th scope="col" className="py-2 font-medium">{copy.table.all}</th>
              </tr>
            </thead>
            <tbody>
              {examples.map(({ mbps: m, e }) => (
                <tr key={m} className="border-b border-border/60">
                  <th scope="row" className="py-2 pr-3 font-medium">{formatNumber(m, lang)} {copy.speedUnit}</th>
                  <td className="py-2 pr-3">{dur(e.liveWithPreviews)}</td>
                  <td className="py-2">{dur(e.allOriginalsWithPreviews)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">{fill(copy.table.note, { mb: formatNumber(DEFAULT_ORIGINAL_MB, lang) })}</p>
        </div>
      </div>
    </div>
  );
}
